import { useCallback, useRef } from 'react';
import type { TransportClock } from '../../../scheduler/clock';
import { computeSpeedMapping } from '../speedMapping';
import { L } from '../../../utils/logger';
import { waitSeekReady } from './helpers/seekHelpers';
import { openGateWhenSignal } from './helpers/health';

export function useSeekPipeline(ctx: {
  tracks: Array<{ role: string; url: string }>,
  refs: any,
  bufferModeRef: React.MutableRefObject<boolean>,
  scheduledRef: React.MutableRefObject<any>,
  logicalStartRef: React.MutableRefObject<number>,
  startedAtRef: React.MutableRefObject<number>,
  masterPlaybackRate: number,
  maintainPitch: boolean,
  status: 'idle' | 'loading' | 'playing' | 'paused',
  pauseAll: () => void,
  scheduleStartAll: (
    ctx: AudioContext,
    scheduled: any,
    whenDelta: number,
    offset: number,
    rate: number,
    perRoleOffset?: Record<string, number>,
    options?: { seamlessSwap?: boolean; fixedLatencySec?: number; clock?: TransportClock }
  ) => number,
  stopAll: (scheduled: any) => void,
  setStatus: (s: any) => void,
  setCurrentTime: (t: number) => void,
  lastSeekAtRef: React.MutableRefObject<number>,
  seekingRef: React.MutableRefObject<{ runId: number; target: number } | null>,
  openTransportGate: (ms?: number) => void,
  closeTransportGate: () => void,
  duration: number,
  resumeAfterLoadRef: React.MutableRefObject<boolean>,
  userPausedRef: React.MutableRefObject<boolean>,
}) {
  const seekRunIdRef = useRef(0);

  const endSeekingAfter = (runId: number, ms = 600) => {
    setTimeout(() => { if (ctx.seekingRef.current?.runId === runId) ctx.seekingRef.current = null; }, ms);
  };

  const seek = useCallback(async (time: number, resume = true) => {
    const runId = ++seekRunIdRef.current;
  const { tracks, refs, bufferModeRef, scheduledRef, logicalStartRef, startedAtRef, masterPlaybackRate, maintainPitch, status, pauseAll, scheduleStartAll, stopAll, setStatus, setCurrentTime, lastSeekAtRef, seekingRef, openTransportGate, closeTransportGate, duration, resumeAfterLoadRef, userPausedRef } = ctx;
    if (!tracks.length) return;

    // Cool drift controller and start seeking session
    const nowMs = (typeof performance !== 'undefined' ? performance.now() : Date.now());
    lastSeekAtRef.current = nowMs;
    const targetT = Math.max(0, Math.min((duration || Infinity) - 0.075, time));
    seekingRef.current = { runId, target: targetT };

    // Hard mute master during seek
    closeTransportGate();

  const wasPlaying = status === 'playing';
    if (bufferModeRef.current && scheduledRef.current && refs.audioCtxRef.current) {
      try { pauseAll(); } catch {}
      stopAll(scheduledRef.current);
      ctx.logicalStartRef.current = targetT;
      setCurrentTime(targetT);
  if (resume && (wasPlaying || resumeAfterLoadRef.current) && !userPausedRef.current && ctx.seekingRef.current?.runId === runId) {
        const actx = refs.audioCtxRef.current as AudioContext;
        // Re-anchor clock at the requested seek target so scheduling uses the new timeline
        try {
          (refs as any).transportClockRef = (refs as any).transportClockRef || { current: null };
          (refs as any).transportClockRef.current = {
            t0Audio: actx.currentTime,
            t0Media: logicalStartRef.current,
            rate: masterPlaybackRate,
          } as TransportClock;
          try { L.info('media:transport', 'clock.anchor.seek', (refs as any).transportClockRef.current); } catch {}
        } catch {}
        const fn = (refs as any).reanchorClockAndReschedule as (() => number | void) | undefined;
        const when = typeof fn === 'function' ? (fn() as number | undefined) : ctx.scheduleStartAll(
          actx,
          scheduledRef.current,
          0.05,
          logicalStartRef.current,
          masterPlaybackRate,
          undefined,
          { fixedLatencySec: maintainPitch ? 0.04 : 0, clock: (refs as any).transportClockRef?.current }
        );
        // After scheduling, set RB parameters according to current mapping
        try {
          if (maintainPitch) {
            const map = computeSpeedMapping(masterPlaybackRate, true);
            const st = (refs as any).pitchStRef?.current ?? 0;
            const userPitch = Math.pow(2, (Number(st) || 0) / 12);
            if (masterPlaybackRate <= 1.0 + 1e-3) {
              for (const s of Object.values(scheduledRef.current || {})) { (s as any).worklet?.setTempo?.(map.rbTempo); (s as any).worklet?.setPitch?.(userPitch); }
              (refs as any).rbTempoRef = (refs as any).rbTempoRef || { current: null };
              (refs as any).rbTempoRef.current = map.rbTempo;
              try { (window as any).__rbLastPitch = userPitch; } catch {}
              // Ensure WA playbackRate returns to 1.0 for active sources post-reschedule
              try {
                for (const s of Object.values(scheduledRef.current || {})) {
                  const stem: any = s;
                  const pr: AudioParam | undefined = (stem.source as any)?.playbackRate;
                  if (pr) { try { (pr as any).value = map.webAudioRate; } catch {} }
                }
              } catch {}
            } else {
              const combined = map.rbPitch * userPitch;
              for (const s of Object.values(scheduledRef.current || {})) { (s as any).worklet?.setTempo?.(1.0); (s as any).worklet?.setPitch?.(combined); }
              (refs as any).rbTempoRef = (refs as any).rbTempoRef || { current: null };
              (refs as any).rbTempoRef.current = 1.0;
              try { (window as any).__rbLastPitch = userPitch; } catch {}
            }
          }
        } catch {}
  if (typeof when === 'number') startedAtRef.current = when;
  setStatus('playing');
  try { openGateWhenSignal(refs); } catch {}
  // Seeking session can end immediately after successful reschedule to avoid pinning UI at target
  try { if (ctx.seekingRef.current?.runId === runId) ctx.seekingRef.current = null; } catch {}
  endSeekingAfter(runId, 600);
      } else {
        setStatus('paused');
      }
      return;
    }

    // Element-mode
    pauseAll();
    setStatus('paused');

    // Zero delay-lines
    try {
      const actx0 = refs.audioCtxRef.current; const now0 = actx0?.currentTime ?? 0;
      const delays = refs.delaysRef.current;
      for (const role of Object.keys(delays)) {
        const d = delays[role]; if (!d) continue;
        try {
          if (actx0) {
            d.delayTime.cancelScheduledValues(now0);
            d.delayTime.setValueAtTime(0, now0);
          } else {
            d.delayTime.value = 0;
          }
        } catch {}
      }
    } catch {}

    // 1) seek + ready
    const tasks: Promise<void>[] = [];
    for (const t of tracks) {
      const el = refs.elsRef.current[t.role];
      if (el) tasks.push(waitSeekReady(el, targetT));
    }
    try { await Promise.all(tasks); } catch {}
    setCurrentTime(targetT);

  if (resume && (wasPlaying || resumeAfterLoadRef.current) && !userPausedRef.current && ctx.seekingRef.current?.runId === runId) {
      // 2) start all
      for (const t of tracks) {
        const el = refs.elsRef.current[t.role];
        if (!el) continue;
        try { (el as any).defaultPlaybackRate = masterPlaybackRate; (el as any).playbackRate = masterPlaybackRate; await el.play(); } catch {}
      }

      // 3) settle + align
      const els = tracks.map(t => refs.elsRef.current[t.role]).filter(Boolean) as HTMLAudioElement[];
      const settle = (el: HTMLMediaElement) => new Promise<void>(resolve => {
        let to: any = null;
        const on = () => { if (to) clearTimeout(to); try { el.removeEventListener('playing', on); } catch {} resolve(); };
        try { el.addEventListener('playing', on, { once: true } as any); } catch { resolve(); return; }
        to = setTimeout(on, 500);
      });
      try { await Promise.allSettled(els.map(settle)); } catch {}
      const playedAt: Record<string, number> = {};
      const playingPromises = tracks.map(tr => new Promise<void>((resolve) => {
        const role = tr.role; const el = refs.elsRef.current[role]; if (!el) { resolve(); return; }
        let to: any = null;
        const on = () => { if (to) clearTimeout(to); try { el.removeEventListener('playing', on); } catch {} playedAt[role] = (typeof performance !== 'undefined' ? performance.now() : Date.now()); resolve(); };
        try { el.addEventListener('playing', on, { once: true } as any); } catch { resolve(); return; }
        to = setTimeout(on, 600);
      }));
      try { await Promise.allSettled(playingPromises); } catch {}
      if (Object.keys(playedAt).length) {
        const tMax = Math.max(...Object.values(playedAt));
        const actx = refs.audioCtxRef.current; const nowA = (actx as any)?.currentTime ?? 0;
        for (const tr of tracks) {
          const role = tr.role; const dnode = refs.delaysRef.current[role]; if (!dnode) continue;
          const ts = playedAt[role] ?? tMax; const deltaMs = Math.max(0, tMax - ts);
          const addS = Math.min(0.06, deltaMs / 1000);
          try {
            if (actx) { dnode.delayTime.cancelScheduledValues(nowA); dnode.delayTime.setValueAtTime(addS, nowA); dnode.delayTime.linearRampToValueAtTime(0, nowA + 0.25); }
            else { dnode.delayTime.value = addS; setTimeout(() => { try { dnode.delayTime.value = 0; } catch {} }, 250); }
          } catch {}
        }
      }

      // 4) delayed snap + micro-corrections + reclamps
      const leaderT = targetT;
      const doSeek = (e: HTMLMediaElement, tVal: number) => {
        try {
          const fast = (e as any).fastSeek as undefined | ((t: number) => void);
          if (typeof fast === 'function') fast(tVal);
          else e.currentTime = tVal;
        } catch {}
      };
      requestAnimationFrame(() => requestAnimationFrame(() => {
        if (seekRunIdRef.current !== runId) return;
        for (const e of els) doSeek(e, leaderT);
      }));
      const baseRate = masterPlaybackRate; const actx2 = refs.audioCtxRef.current; const nowA2 = (actx2 as any)?.currentTime ?? 0;
      for (const tr of tracks) {
        const role = tr.role; const el = refs.elsRef.current[role]; if (!el) continue;
        const err = (el.currentTime || 0) - leaderT;
        if (err > 0) {
          const d = refs.delaysRef.current[role]; if (d) {
            const target = Math.min(0.03, err);
            try {
              if (actx2) { d.delayTime.cancelScheduledValues(nowA2); d.delayTime.setValueAtTime(target, nowA2); d.delayTime.linearRampToValueAtTime(0, nowA2 + 0.25); }
              else { d.delayTime.value = target; setTimeout(() => { try { d.delayTime.value = 0; } catch {} }, 250); }
            } catch {}
          }
        } else if (err < 0) {
          const boost = Math.min(baseRate + Math.min(0.03, Math.abs(err) / 1.0), baseRate + 0.03);
          try { (el as any).playbackRate = boost; } catch {}
          setTimeout(() => { if (seekRunIdRef.current !== runId) return; try { (el as any).playbackRate = baseRate; } catch {} }, 250);
        }
      }
      const reclamps = [120, 260, 520, 1000];
      for (const ms of reclamps) {
        setTimeout(() => {
          if (seekRunIdRef.current !== runId) return;
          for (const e of els) {
            const err = (e.currentTime || 0) - leaderT;
            if (Math.abs(err) > 0.02) doSeek(e, leaderT);
          }
        }, ms);
      }

      // go live
      lastSeekAtRef.current = (typeof performance !== 'undefined' ? performance.now() : Date.now());
  setStatus('playing');
  try { openGateWhenSignal(refs); } catch {}
  // Release seeking pin right away after element-mode seek/sync
  try { if (ctx.seekingRef.current?.runId === runId) ctx.seekingRef.current = null; } catch {}
  endSeekingAfter(runId, 600);
    }
  }, [ctx]);

  return { seek };
}
